import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { MODALITIES, PACES, type Modality, type Pace } from "@/lib/modality";
import { WattsLogo } from "@/components/WattsLogo";

export const Route = createFileRoute("/onboarding")({
  component: Onboarding,
});

function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [step, setStep] = useState<0 | 1>(0);
  const [modality, setModality] = useState<Modality | null>(null);
  const [pace, setPace] = useState<Pace | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/auth" });
  }, [user, loading, navigate]);

  const finish = async () => {
    if (!user || !modality || !pace) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        preferred_modality: modality,
        preferred_pace: pace,
        onboarding_completed: true,
      } as never)
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      console.error("[onboarding] profile update failed", error);
      toast.error(`${t("profile.onboarding.saveError")} (${error.message})`);
      return;
    }
    navigate({ to: "/map" });
  };

  return (
    <div
      className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pt-12 pb-8"
      style={{ background: "#0f0f0f", fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <WattsLogo width={120} />
        <div className="flex gap-1.5">
          {[0, 1].map((i) => (
            <span
              key={i}
              className="h-1 w-10 rounded-full"
              style={{ background: i <= step ? "#F5C518" : "#2a2a2a" }}
            />
          ))}
        </div>
      </div>

      <div className="flex-1">
        {step === 0 && (
          <div>
            <h1
              className="text-white"
              style={{
                fontFamily: "'Barlow Condensed', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 36,
                lineHeight: 1.05,
              }}
            >
              {t("profile.onboarding.step1.title")}
            </h1>
            <p className="mt-3 text-[#888]">{t("profile.onboarding.step1.description")}</p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {MODALITIES.map((m) => {
                const selected = modality === m.value;
                return (
                  <button
                    key={m.value}
                    onClick={() => setModality(m.value)}
                    className="press-scale relative flex aspect-square flex-col items-center justify-center gap-3 rounded-2xl border-2 p-4 transition-all"
                    style={{
                      borderColor: selected ? "#F5C518" : "#2a2a2a",
                      background: "#1a1a1a",
                    }}
                  >
                    <m.Icon
                      className="h-10 w-10"
                      style={{ color: selected ? "#F5C518" : "#fff" }}
                      strokeWidth={1.8}
                    />
                    <span className="font-semibold text-white">{m.label}</span>
                    {selected && (
                      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#F5C518]">
                        <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h1
              className="text-white"
              style={{
                fontFamily: "'Barlow Condensed', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 36,
                lineHeight: 1.05,
              }}
            >
              {t("profile.onboarding.step2.title")}
            </h1>
            <p className="mt-3 text-[#888]">{t("profile.onboarding.step2.description")}</p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {PACES.map((p) => {
                const selected = pace === p.value;
                return (
                  <button
                    key={p.value}
                    onClick={() => setPace(p.value)}
                    className="press-scale relative flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 transition-all"
                    style={{
                      borderColor: selected ? "#F5C518" : "#2a2a2a",
                      background: "#1a1a1a",
                    }}
                  >
                    <p.Icon
                      className="h-9 w-9"
                      style={{ color: selected ? "#F5C518" : "#fff" }}
                      strokeWidth={1.8}
                    />
                    <span className="font-semibold text-white">{p.label}</span>
                    <span className="text-xs text-[#888]">{p.hint}</span>
                    {selected && (
                      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#F5C518]">
                        <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer / actions */}
      <div className="mt-6 space-y-3">
        {step === 0 ? (
          <button
            disabled={!modality}
            onClick={() => setStep(1)}
            className="press-scale flex w-full items-center justify-center gap-2 rounded-full py-4 font-bold disabled:opacity-40"
            style={{ background: "#F5C518", color: "#000" }}
          >
            {t("profile.onboarding.next")} <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            disabled={!pace || saving}
            onClick={finish}
            className="press-scale flex w-full items-center justify-center gap-2 rounded-full py-4 font-bold disabled:opacity-40"
            style={{ background: "#F5C518", color: "#000" }}
          >
            {saving ? "…" : t("profile.onboarding.finish")} <ArrowRight className="h-4 w-4" />
          </button>
        )}

        {step > 0 && (
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1) as 0 | 1)}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-[#2a2a2a] bg-[#1a1a1a] py-3 text-sm font-medium text-white"
          >
            <ArrowLeft className="h-4 w-4" /> {t("profile.onboarding.prev")}
          </button>
        )}

        <p className="text-center text-xs text-[#666]">
          {t("profile.onboarding.progress", { current: step + 1, total: 2 })}
        </p>
      </div>
    </div>
  );
}
